import { useCallback, useEffect } from 'react'
import { getFieldRegistration, getFieldValidationAttributes } from '../../utils/formValidation'
import WifiEthernetSection from './WifiEthernetSection'

function NetworkSection({ register, errors, watch, onBlur, getFieldError, setValue, deviceInfo, isInternetConnected, serialNumber }) {
  const formData = watch()

  const handleFieldBlur = useCallback((fieldName) => (e) => {
    if (onBlur) {
      onBlur(fieldName, e.target.value)
    }
  }, [onBlur])

  const renderFieldError = useCallback((fieldName) => {
    const error = getFieldError ? getFieldError(fieldName) : errors[fieldName]
    return error && <div className="text-danger">{error.message}</div>
  }, [getFieldError, errors])

  // Clear CAN bus required switches when both protocols are disabled
  useEffect(() => {
    if (!formData.canbusRvc && !formData.canbusMarine) {
      setValue('canbusRequired', false)
      setValue('canbusRequired2', false)
    }
  }, [formData.canbusRvc, formData.canbusMarine, setValue])

  // BMS protocol auto-adjustments (protocol selection, bitrate defaults, RV-C/NMEA 2000 compatibility)
  useEffect(() => {
    if (formData.enableBmsProtocols) {
      // Auto-select Victron when enabled
      if (!formData.bmsProtocol || formData.bmsProtocol === 'none') {
        setValue('bmsProtocol', 'victron')
      }
      // Set default bitrate if not already set
      if (!formData.bmsBitrate) {
        setValue('bmsBitrate', '250000')
      }
      // Disable RV-C/NMEA 2000 at 500 kbps (incompatible bitrates)
      if (formData.bmsBitrate === '500000') {
        if (formData.canbusRvc) setValue('canbusRvc', false)
        if (formData.canbusMarine) setValue('canbusMarine', false)
      }
    } else {
      // Reset protocol when disabled
      if (formData.bmsProtocol && formData.bmsProtocol !== 'none') {
        setValue('bmsProtocol', 'none')
      }
      // Reset to 250 kbps so RV-C/NMEA 2000 can work
      if (formData.bmsBitrate === '500000') {
        setValue('bmsBitrate', '250000')
      }
    }
  }, [formData.enableBmsProtocols, formData.bmsProtocol, formData.bmsBitrate, formData.canbusRvc, formData.canbusMarine, setValue])

  // Reusable input field renderer
  const renderNumberInput = useCallback((fieldName, label, helpText, conditional = true) => {
    if (!conditional) return null

    return (
      <div className="mb-3">
        <label htmlFor={fieldName} className="form-label">
          {label}
          {helpText && (
            <i
              className="bi bi-info-circle ms-1"
              data-bs-toggle="popover"
              data-bs-trigger="click"
              data-bs-content={helpText}
            ></i>
          )}
        </label>
        <input
          type="number"
          className={`form-control ${getFieldError && getFieldError(fieldName) ? 'is-invalid' : ''}`}
          id={fieldName}
          {...getFieldValidationAttributes(fieldName, formData)}
          {...register(fieldName, getFieldRegistration(fieldName, formData))}
          onBlur={handleFieldBlur(fieldName)}
        />
        {renderFieldError(fieldName)}
      </div>
    )
  }, [formData, register, getFieldError, handleFieldBlur, renderFieldError])

  // Dynamic battery instance fields based on numBatsInBank
  const renderBatteryInstances = useCallback((suffix = '') => {
    const numBats = parseInt(formData[`numBatsInBank${suffix}`] || 1, 10)
    const instanceLabels = ['', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J']

    return Array.from({ length: Math.min(numBats, 10) }, (_, i) => {
      const fieldName = `batteryInstance${instanceLabels[i]}${suffix}`
      return (
        <input
          key={fieldName}
          type="number"
          className={`form-control mt-1 ${getFieldError && getFieldError(fieldName) ? 'is-invalid' : ''}`}
          id={fieldName}
          {...getFieldValidationAttributes(fieldName, formData)}
          {...register(fieldName, getFieldRegistration(fieldName, formData))}
          onBlur={handleFieldBlur(fieldName)}
        />
      )
    })
  }, [formData, register, getFieldError, handleFieldBlur])

  // Reusable alternator section renderer
  const renderAlternatorSection = useCallback((suffix = '', title = 'Alternator one') => (
    <div id={`canbusAlt${suffix}`}>
      <h4 className="mt-3">{title}</h4>
      {renderNumberInput(
        `chargerInstance${suffix}`,
        'Charger instance',
        'For NMEA 2000/VE.Can: Choose an instance NOT used by any battery or charger (instances start at 0; default battery is 0, so use 1+ for chargers). For RV-C: Choose any instance starting from 1 (batteries and chargers can share instance numbers). Leave blank to disable setting.',
      )}
      {renderNumberInput(
        `engineInstance${suffix}`,
        'Engine instance',
        'Instance or device ID for the engine connected to the Altion. RV-C and J1939 only support instance 1. NMEA 2000 default is 0. Leave blank to disable setting.',
      )}
      {renderNumberInput(
        `engineLoadThreshold${suffix}`,
        'Engine load threshold to disable alternator (%)',
        'Uses CAN bus data, if available, to determine the amount of torque that the engine is generating as a percent of its maximum. Typical setting is 60-80%. Leave blank to disable.',
        formData[`engineInstance${suffix}`],
      )}
    </div>
  ), [formData, renderNumberInput])

  // Reusable battery bank section renderer
  const renderBatteryBankSection = useCallback((suffix = '', title = 'Battery bank one') => (
    <div id={`canbusBat${suffix}`}>
      <h4 className="mt-3">{title}</h4>
      {renderNumberInput(
        `numBatsInBank${suffix}`,
        'Number of batteries broadcasting in bank',
        'If multiple batteries broadcast CAN bus data that must be combined to make a single bank, enter the number of batteries. Otherwise this should be 1. Leave blank to disable setting.',
      )}

      <div className="mb-3">
        <label htmlFor={`batteryInstance${suffix}`} className="form-label">
          Instance(s)
          <i
            className="bi bi-info-circle ms-1"
            data-bs-toggle="popover"
            data-bs-trigger="click"
            data-bs-content="Instance(s) or device ID(s) for the batteries. For NMEA 2000/VE.Can: Default battery instance is 0. For RV-C: Default battery instance is 1. Leave blank to disable setting."
          ></i>
        </label>
        {renderBatteryInstances(suffix)}
      </div>

      <div className="form-check form-switch mb-3">
        <input
          className="form-check-input"
          type="checkbox"
          role="switch"
          id={`canbusRequired${suffix}`}
          {...register(`canbusRequired${suffix}`)}
        />
        <label className="form-check-label" htmlFor={`canbusRequired${suffix}`}>
          Require canbus to operate&nbsp;
        </label>
        <i
          className="bi bi-info-circle ms-1"
          data-bs-toggle="popover"
          data-bs-trigger="click"
          data-bs-content="This will cutoff charging if battery bank voltage messages discontinue."
        ></i>
      </div>
    </div>
  ), [renderNumberInput, renderBatteryInstances, register])

  return (
    <div
      className="card shadow-sm border-0 mb-4"
      style={{
        background: 'linear-gradient(to right, #ffffff, #f8f9fa)',
        transition: 'all 0.3s ease',
      }}
    >
      {/* Card Header - Modern Design */}
      <div className="card-header fw-bold border-0 fs-5">
        <i className="bi bi-wifi text-primary fs-3 me-2" aria-hidden="true"></i>
        <span>Networking & Software Update</span>
      </div>

      {/* Card Body */}
      <div className="card-body">
        {/* CAN bus Section */}
        <h4 className="mb-3 d-flex align-items-center">
          <i className="bi bi-hdd-network text-info me-2"></i>
          CAN bus
        </h4>
        <h5 className="mb-3">Protocols enabled</h5>

        <div className="form-check form-switch mb-3">
          <input
            className="form-check-input"
            type="checkbox"
            role="switch"
            id="canbusRvc"
            {...register('canbusRvc')}
          />
          <label className="form-check-label" htmlFor="canbusRvc">
            RV-C
          </label>
        </div>

        <div className="form-check form-switch mb-3">
          <input
            className="form-check-input"
            type="checkbox"
            role="switch"
            id="canbusMarine"
            {...register('canbusMarine')}
          />
          <label className="form-check-label" htmlFor="canbusMarine">
            NMEA 2000
          </label>
          <i
            className="bi bi-info-circle ms-1"
            data-bs-toggle="popover"
            data-bs-trigger="click"
            data-bs-content="Also used for Victron VE.Can devices, including Victron Lynx Smart BMS."
            style={{ cursor: 'pointer' }}
          ></i>
        </div>

        {/* Additional BMS Protocols Switch */}
        <div className="form-check form-switch mb-3">
          <input
            className="form-check-input"
            type="checkbox"
            role="switch"
            id="enableBmsProtocols"
            {...register('enableBmsProtocols')}
          />
          <label className="form-check-label" htmlFor="enableBmsProtocols">
            Additional Battery Management System (BMS) Protocols&nbsp;
          </label>
          <i
            className="bi bi-info-circle ms-1"
            data-bs-toggle="popover"
            data-bs-trigger="click"
            data-bs-content="Enable this to configure direct communication with compatible Battery Management Systems for receiving charge/discharge limits and battery state data."
          ></i>
        </div>

        {/* BMS Protocol Selection - Only show when switch is enabled */}
        {formData.enableBmsProtocols && (
          <div className="ms-4 mb-3 p-3 bg-light rounded border-start border-info border-3">
            <label className="form-label fw-medium">
              BMS Protocol
            </label>

            <div className="form-check">
              <input
                className="form-check-input"
                type="radio"
                id="bmsProtocolVictron"
                value="victron"
                {...register('bmsProtocol')}
              />
              <label className="form-check-label" htmlFor="bmsProtocolVictron">
                Victron CAN-BMS
              </label>
              <i
                className="bi bi-info-circle ms-1"
                data-bs-toggle="popover"
                data-bs-trigger="click"
                data-bs-content="For 3rd party batteries that use the Victron CAN-BMS protocol. NOT for Victron Lynx Smart BMS, GX devices, or other Victron products (which use VE.Can/VE.Bus)."
                style={{ cursor: 'pointer' }}
              ></i>
            </div>

            {/* Victron BMS Bitrate - Conditional on Victron selection */}
            {formData.bmsProtocol === 'victron' && (
              <div className="mt-3 ms-3 p-3 bg-white rounded border">
                <label className="form-label fw-medium">
                  Bitrate
                  <i
                    className="bi bi-info-circle ms-1"
                    data-bs-toggle="popover"
                    data-bs-trigger="click"
                    data-bs-content="Select the CAN bus bitrate that matches your BMS configuration. Most systems use 250 kbps (default) which is compatible with RV-C and NMEA 2000. Some systems may use 500 kbps, but note that RV-C and NMEA 2000 cannot be used at 500 kbps as they require 250 kbps. Check your BMS documentation."
                  ></i>
                </label>

                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    id="bmsBitrate250"
                    value="250000"
                    {...register('bmsBitrate')}
                  />
                  <label className="form-check-label" htmlFor="bmsBitrate250">
                    250 kbps (default)
                  </label>
                </div>

                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    id="bmsBitrate500"
                    value="500000"
                    {...register('bmsBitrate')}
                  />
                  <label className="form-check-label" htmlFor="bmsBitrate500">
                    500 kbps
                  </label>
                </div>

                {/* Warning about RV-C and NMEA 2000 incompatibility with 500 kbps */}
                {formData.bmsBitrate === '500000' && (
                  <div className="alert alert-warning mt-2 mb-0" role="alert">
                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                    <strong>Note:</strong> RV-C and NMEA 2000 protocols operate at 250 kbps and cannot be used simultaneously with 500 kbps Victron BMS. They will be automatically disabled.
                  </div>
                )}

                {/* Show validation error if bitrate is not selected */}
                {renderFieldError('bmsBitrate')}
              </div>
            )}
          </div>
        )}

        {/* CAN bus Details - only show if protocols are enabled */}
        {(formData.canbusRvc || formData.canbusMarine) && (
          <div id="canbusDetails">
            {/* Alternator CAN Settings */}
            {renderAlternatorSection('', 'Alternator one')}

            {/* Alternator Two CAN Settings - only if two alternators */}
            {formData.numAlternatorsInstalled === 'numAlternatorsInstalled2' &&
              renderAlternatorSection('2', 'Alternator two')
            }

            {/* Battery Bank CAN Settings */}
            {renderBatteryBankSection('', 'Battery bank one')}

            {/* Battery Bank Two CAN Settings - only if Battery Bank Two exists */}
            {formData.numBatteriesManaged === 'numBatteriesManaged2' &&
              renderBatteryBankSection('2', 'Battery bank two')
            }
          </div>
        )}

        {/* Wi-Fi / Ethernet Section */}
        <WifiEthernetSection
          register={register}
          errors={errors}
          watch={watch}
          onBlur={onBlur}
          getFieldError={getFieldError}
          setValue={setValue}
        />

        {/* Configuration Lock */}
        <h3 className="mt-3">Configuration lock</h3>
        <div className="mb-3">
          <label htmlFor="configPassword" className="form-label">
            Configuration password (leave blank for none)
            <i
              className="bi bi-info-circle ms-1"
              data-bs-toggle="popover"
              data-bs-trigger="click"
              data-bs-content="Optional: Create a password to edit configuration. If password is lost, factory reset device."
            ></i>
          </label>
          <input
            type="text"
            className={`form-control ${getFieldError && getFieldError('configPassword') ? 'is-invalid' : ''}`}
            id="configPassword"
            {...register('configPassword')}
            onBlur={handleFieldBlur('configPassword')}
          />
          {renderFieldError('configPassword')}
        </div>

        {/* Revatek Connect */}
        <h3 className="mt-3">Revatek Connect</h3>

        {/* Current Software Version - Styled */}
        <div className="mb-3 p-3 bg-light rounded border-start border-primary border-3">
          <div className="d-flex align-items-center">
            <i className="bi bi-cpu text-primary me-2"></i>
            <span className="fw-medium text-muted me-2">Current software version:</span>
            <span className="badge bg-primary fw-normal" id="regulatorSoftwareVersion">
              {deviceInfo?.regulatorSoftwareVersion || 'Loading...'}
            </span>
          </div>
        </div>

        <div className="form-check form-switch mb-3">
          <input
            className="form-check-input"
            type="checkbox"
            role="switch"
            id="autoUpdateSwitch"
            {...register('autoUpdateSwitch')}
          />
          <label className="form-check-label" htmlFor="autoUpdateSwitch">
            Enable Revatek Connect&nbsp;
          </label>
          <i
            className="bi bi-info-circle ms-1"
            data-bs-toggle="popover"
            data-bs-trigger="click"
            data-bs-content="<strong>Recommended:</strong> Enables Revatek Connect cloud platform with automatic software updates, remote monitoring, analysis, configuration and support. Get seamless over-the-air updates plus full cloud access to your system from anywhere. Requires internet connection via Wi-Fi or Ethernet. Updates install automatically within 20 minutes when available."
          ></i>
        </div>

        {/* Marketing benefit callout */}
        <div className="alert alert-info d-flex align-items-start" role="alert">
          <i className="bi bi-cloud-check-fill me-2 mt-1 text-primary" style={{ fontSize: '1.1rem' }}></i>
          <div>
            <strong>Unlock the full potential of your Revatek system!</strong>
            <br />
            <small className="text-muted">
              Enable Revatek Connect for automatic updates, remote monitoring, analysis, configuration and support
              from anywhere in the world.
            </small>
          </div>
        </div>

        {/* Revatek Connect Device Registration - Only show if internet is available and we have a serial number */}
        {isInternetConnected && serialNumber && (
          <div className="card shadow-sm border-0 mb-3" style={{ borderLeft: '4px solid #0d6efd' }} data-component="RevatekConnectRegistration">
            <div className="card-body py-3">
              <div className="d-flex align-items-center">
                <i className="bi bi-person-plus-fill me-3 text-primary fs-4" aria-hidden="true"></i>
                <div className="flex-grow-1 me-4">
                  <h6 className="card-title mb-1">Add Device to Revatek Connect</h6>
                  <p className="card-text mb-0 text-muted small">
                    Register this device with your Revatek Connect account for seamless access
                  </p>
                </div>
                <div className="flex-shrink-0">
                  <a
                    href={`https://connect.revatek.com/add-device?serial=${encodeURIComponent(serialNumber)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-outline-primary px-3"
                    style={{ fontSize: '0.875rem' }}
                  >
                    <i className="bi bi-box-arrow-up-right me-1"></i>
                    Add to My Account
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default NetworkSection
